// usePlacementNavigation — single write-path authority for Design Lab
// placement transitions (MN-9145, follow-up to MN-7799 / PR #59199).
//
// Keeps the placement triple — `designSubTabAtom`, `currentPlacement`, and
// `selectedFrontPlacement` — mutually consistent inside ONE synchronous block
// (a single React 19 commit), so render-time readers of `currentPlacement`
// (Canvas side/artworkSize/artworkDimensions, DesignLabArtboard
// side -> artworkImage) are correct on the first commit after any transition
// instead of settling one render later via the post-commit sync effect in
// `shared/ui/layouts/state.tsx` (which is retained as a safety net for
// direct `designSubTabAtom` writes — see that effect's comment).
//
// Called from: FrontBackSubTabs, SideSwitcher, useRecapture, Canvas
// (handleDragEnd), ImagePlacement, useDesignLibrarySelection, and
// useEnhancementsStarted. Depends on the `artworkUtils` side<->placement
// resolvers and the shared `designSubTabAtom`.
import { useSetAtom } from "jotai";
import { useCallback } from "react";

import { getPlacementFromSide, getSideFromPlacement } from "@design-lab/shared/lib/artworkUtils";
import { designSubTabAtom } from "@design-lab/shared/lib/tab-navigation";
import type { PlacementFormPosition, Side } from "@design-lab/shared/types";
import { useArtworkForm } from "./useArtworkForm";

type PlacementNavigation = {
  setActiveSide: (side: Side) => void;
  commitPlacement: (placement: PlacementFormPosition) => void;
};

// Why: shared write options mirroring `usePlacementPosition.setPlacement` so
// hook-routed writes are indistinguishable from the legacy lens writes to
// react-hook-form subscribers (dirty/touched tracking included).
const WRITE_OPTIONS = { shouldDirty: true, shouldTouch: true } as const;

/**
 * Action hook exposing the two placement-transition writers.
 *
 * Both writers read form values at call time (`form.getValues`) rather than at
 * render time, so they stay correct inside async callbacks (e.g. the
 * post-`await warmCache` design-library path) where render-scoped values may
 * be stale. Both returned callbacks are referentially stable.
 *
 * @returns `setActiveSide` for callers that only know a side, and
 *   `commitPlacement` for callers that know the concrete placement
 */
export function usePlacementNavigation(): PlacementNavigation {
  const { form } = useArtworkForm();
  const setDesignSubTab = useSetAtom(designSubTabAtom);

  /**
   * Switches the active side (Front/Back sub-tab) and synchronously points
   * `currentPlacement` at that side's concrete placement.
   *
   * Why it never writes `selectedFrontPlacement`: side-only callers (sub-tab
   * click, recapture, sub-tab init, upload) do not know the user's front
   * sub-position; force-writing a bare "front" would silently downgrade a
   * "frontLeftChest" selection (the state-model analog of silent zeroing).
   * `getPlacementFromSide` resolves the concrete front placement instead.
   *
   * @param side - the side being activated ("front" | "back")
   */
  const setActiveSide = useCallback(
    (side: Side): void => {
      setDesignSubTab(side);
      form.setValue(
        "currentPlacement",
        getPlacementFromSide(side, form.getValues("selectedFrontPlacement")),
        WRITE_OPTIONS
      );
    },
    [form, setDesignSubTab]
  );

  /**
   * Commits a concrete placement, keeping the whole triple consistent in one
   * synchronous block.
   *
   * Front-family rule: `selectedFrontPlacement` is updated only when the
   * committed placement is on the front side (per `getSideFromPlacement`, not
   * string prefix checks — the placement enum may grow, see PR #58959), so a
   * "back" commit never clobbers the user's front sub-position.
   *
   * @param placement - the concrete placement being committed
   */
  const commitPlacement = useCallback(
    (placement: PlacementFormPosition): void => {
      const side = getSideFromPlacement(placement);
      form.setValue("currentPlacement", placement, WRITE_OPTIONS);
      if (side === "front") {
        form.setValue("selectedFrontPlacement", placement, WRITE_OPTIONS);
      }
      setDesignSubTab(side);
    },
    [form, setDesignSubTab]
  );

  return { setActiveSide, commitPlacement };
}
